

import {
  AccountBalanceWallet,
  Add,
  Delete,
  Edit,
  Groups,
  ImportExport,
  Person,
  Receipt,
  TrendingDown,
  TrendingUp,
  Visibility
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slide,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useAppSelector } from 'app/hooks';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Record,
  clearRecordsError,
  clearRecordsOnGroupChange,
  deleteRecord,
  fetchRecords,
  saveRecord,
  setCurrentYear,
  setFilter,
  setTimeFilter
} from '../../slices/incomeExpenses/IncomeExpensesSlice';
import { RootState } from '../../store';
import IncomeExpensesForm from './IncomeExpensesForm';
import IncomeExpensesImportModal from './IncomeExpensesImportModal';
import DocumentUpload from './DocumentUpload';

// Transition component for dialog
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Format numbers with abbreviations (k, M, B, etc.)
const formatNumberWithAbbreviation = (num: number): string => {
  if (num === 0) return '$0';
  
  const sign = num < 0 ? '-' : '';
  const absNum = Math.abs(num);
  
  const formatAbbrev = (value: number, suffix: string): string => {
    const formatted = value.toFixed(1);
    // Remove .0 for whole numbers
    return formatted.endsWith('.0') ? formatted.slice(0, -2) + suffix : formatted + suffix;
  };
  
  if (absNum >= 1000000000) {
    return `${sign}$${formatAbbrev(absNum / 1000000000, 'B')}`;
  }
  if (absNum >= 1000000) {
    return `${sign}$${formatAbbrev(absNum / 1000000, 'M')}`;
  }
  if (absNum >= 1000) {
    return `${sign}$${formatAbbrev(absNum / 1000, 'k')}`;
  }
  
  return `${sign}$${absNum.toFixed(2)}`;
};

interface SummaryCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'success' | 'error' | 'info' | 'secondary';
  count?: number;
  trendDirection?: 'up' | 'down' | 'neutral';
}

const SummaryCard = ({
  title,
  value,
  icon,
  color,
  count,
  trendDirection = 'neutral'
}: SummaryCardProps) => (
  <Card
    sx={{
      p: 2,
      height: '100%',
      borderRadius: 2,
      border: `1px solid`,
      borderColor: 'divider',
      background: `linear-gradient(180deg, ${color}.lighter 0%, #fff 60%)`,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 3
      }
    }}
  >
    <Stack spacing={2}>
      {/* Title & Trend */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {title}
        </Typography>
        {trendDirection !== 'neutral' && (
          <Box
            sx={{
              color: trendDirection === 'up' ? 'success.main' : 'error.main',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {trendDirection === 'up' ? (
              <TrendingUp fontSize="small" />
            ) : (
              <TrendingDown fontSize="small" />
            )}
          </Box>
        )}
      </Box>

      {/* Icon & Value */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {icon}
        <Box>
          <Typography variant="h5" fontWeight={700} color={`${color}.main`} sx={{ lineHeight: 1 }}>
            {formatNumberWithAbbreviation(value)}
          </Typography>
          {count !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {count} {count === 1 ? 'record' : 'records'}
            </Typography>
          )}
        </Box>
      </Box>
    </Stack>
  </Card>
);

const IconWrapper = ({
  icon,
  color,
  isMobile
}: {
  icon: React.ReactNode;
  color: 'success' | 'error' | 'info' | 'secondary';
  isMobile: boolean;
}) => (
  <Box
    sx={{
      backgroundColor: `${color}.light`,
      borderRadius: '50%',
      p: 1.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'transform 0.3s ease',
      '&:hover': {
        transform: 'scale(1.1)'
      }
    }}
  >
    {React.cloneElement(icon as React.ReactElement<any>, {
      sx: {
        color: `${color}.main`,
        fontSize: isMobile ? 28 : 32
      }
    })}
  </Box>
);

const IncomeExpensesList = () => {
  const handleUploadDocument = async (record: Record, file: File | null) => {
    console.log('🔵 Upload handler called with file:', { fileName: file?.name, fileSize: file?.size, record: record.id });
    
    if (!file) {
      console.error('❌ No file selected');
      return;
    }
    
    if (!record.id || !selectedProjectId || !selectedServiceTitle) {
      console.error('❌ Missing required fields:', {
        recordId: record.id,
        selectedProjectId,
        selectedServiceTitle
      });
      setSnackbar({
        open: true,
        message: 'Missing record or project details.',
        severity: 'error'
      });
      return;
    }

    try {
      console.log('🔵 Starting file read for base64 conversion...');
      
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          console.log('🟢 File read complete, converting to base64...');
          // Remove data URL prefix (e.g., "data:image/png;base64,")
          const base64 = result.split(',')[1];
          console.log('🟢 Base64 conversion complete, length:', base64.length);
          resolve(base64);
        };
        reader.onerror = (error) => {
          console.error('❌ File read error:', error);
          reject(error);
        };
      });
      reader.readAsDataURL(file);
      const fileBase64 = await base64Promise;

      const uploadPayload = {
        group_id: selectedProjectId,
        service_id: selectedServiceTitle,
        record_id: record.id,
        current_year,
        current_date: dayjs().format('YYYY-MM-DD'),
        record_data: {
          id: record.id,
          doc_file: {
            fileName: file.name,
            contentType: file.type,
            fileBase64: fileBase64
          }
        }
      };
      
      console.log('🔵 Document Upload - Payload being sent:', {
        recordId: uploadPayload.record_data.id,
        recordIdTopLevel: uploadPayload.record_id,
        groupId: uploadPayload.group_id,
        serviceId: uploadPayload.service_id,
        currentYear: uploadPayload.current_year,
        fileName: file.name,
        fullPayload: uploadPayload
      });

      const resultAction = await dispatch(
        saveRecord(uploadPayload as any)
      );

      console.log('🟢 Dispatch result:', resultAction);

      if ('payload' in resultAction && resultAction.payload) {
        console.log('🟢 Document uploaded successfully:', resultAction.payload);
        setSnackbar({
          open: true,
          message: 'Document uploaded successfully.',
          severity: 'success'
        });
        // Force full refresh to get updated record from backend
        console.log('🔵 Fetching records to refresh...');
        await dispatch(
          fetchRecords({
            group_id: selectedProjectId,
            service_id: selectedServiceTitle,
            transaction_type: filter.toLowerCase(),
            current_year,
            time_filter,
            page_size: 50,
            continuation_token: null
          })
        );
        console.log('🟢 Records refreshed');
      } else {
        console.error('❌ Upload failed - no payload in result');
        setSnackbar({
          open: true,
          message: 'Failed to upload document.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('❌ Error uploading document:', error);
      setSnackbar({
        open: true,
        message: 'Failed to process document.',
        severity: 'error'
      });
    }
  };
  // Get user details and roles from localStorage (for RBAC)
  const userDetails = React.useMemo(() => {
    try {
      const str = localStorage.getItem('user-details');
      const parsed = str ? JSON.parse(str) : {};
      console.log('IncomeExpensesList - User Details:', parsed);
      return parsed;
    } catch {
      return {};
    }
  }, []);
  const userEmail = userDetails?.email || '';
  const userRoles = (userDetails?.role || []).map((r:any) => (typeof r === 'string' ? r : r?.name || r?.label || ''));
  const isAdmin = userRoles.some((r:string) => r.toLowerCase() === 'admin');
  
  console.log('IncomeExpensesList - User Email:', userEmail);
  console.log('IncomeExpensesList - User Roles:', userRoles);
  console.log('IncomeExpensesList - Is Admin:', isAdmin);

  // Group admin logic: get group admins from somewhere (ideally from group context or API, fallback to userDetails.groups)
  // For now, assume user is group admin if their email is in selectedGroup.admins/administrators (fallback to empty array)
  const selectedGroup = useAppSelector((state) => state.loginSignup?.selectedGroup);
  // Accept both possible admin fields, fallback to []
  const groupAdmins: string[] = Array.isArray((selectedGroup as any)?.administrators)
    ? (selectedGroup as any).administrators
    : Array.isArray((selectedGroup as any)?.admins)
    ? (selectedGroup as any).admins
    : [];
  const isGroupAdmin = groupAdmins.includes(userEmail) || isAdmin;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    records,
    summary,
    loading,
    error,
    filter,
    current_year,
    time_filter,
    nextToken,
    hasMoreRecords
  } = useSelector((state: RootState) => state.incomeExpenses);
  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );
  const [openModal, setOpenModal] = React.useState(false);
  const [openImportModal, setOpenImportModal] = React.useState(false);
  const [editData, setEditData] = React.useState<Record | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<Record | null>(null);

  // Effect to clear records when group_id changes
  useEffect(() => {
    if (selectedProjectId) {
      dispatch(clearRecordsOnGroupChange(selectedProjectId));
    }
  }, [selectedProjectId, dispatch]);

  // Effect to fetch records when on /income-expenses route
  useEffect(() => {
    if (location.pathname === '/income-expenses' && selectedProjectId && selectedServiceTitle) {
     
      dispatch(
        fetchRecords({
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          transaction_type: filter.toLowerCase(),
          current_year,
          time_filter,
          page_size: 10,
          continuation_token: null
        })
      );
    }
  }, [
    dispatch,
    location.pathname,
    selectedProjectId,
    selectedServiceTitle,
    filter,
    current_year,
    time_filter
  ]);

  const handleLoadMore = () => {
    if (hasMoreRecords && nextToken && selectedProjectId && selectedServiceTitle) {
      dispatch(
        fetchRecords({
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          transaction_type: filter.toLowerCase(),
          current_year,
          time_filter,
          page_size: 10,
          continuation_token: nextToken
        })
      );
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setEditData(null);
  };

  const handleOpenImportModal = () => setOpenImportModal(true);
  const handleCloseImportModal = () => setOpenImportModal(false);

  const handleEdit = (record: Record) => {
    setEditData(record);
    handleOpenModal();
  };

  // Delete confirmation handlers
  const handleDeleteClick = (record: Record) => {
    setRecordToDelete(record);
    setDeleteConfirmOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setRecordToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (recordToDelete?.id && selectedProjectId && selectedServiceTitle) {
      const resultAction = await dispatch(
        deleteRecord({
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          record_id: recordToDelete.id
        }) as any
      );

      if (resultAction.type === deleteRecord.fulfilled.type) {
        setSnackbar({
          open: true,
          message: 'Record deleted successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to delete record',
          severity: 'error'
        });
      }
    }

    setDeleteConfirmOpen(false);
    setRecordToDelete(null);
  };

  const filteredRecords = records
    .filter((record: Record) => (filter === 'all' ? true : record.transactionType === filter))
    .reduce((unique: Record[], record: Record) => {
      // Remove duplicates by keeping only the latest record for each ID
      const existingIndex = unique.findIndex(r => r.id === record.id);
      if (existingIndex === -1) {
        // No duplicate, add the record
        unique.push(record);
      } else {
        // Duplicate found - keep the one with more recent timestamp
        const existing = unique[existingIndex];
        const recordTime = new Date(record.createdAt || 0).getTime();
        const existingTime = new Date(existing.createdAt || 0).getTime();
        if (recordTime > existingTime) {
          unique[existingIndex] = record; // Replace with newer record
        }
      }
      return unique;
    }, []);

  const canViewDocument = (record: Record) => {
    if (!record.doc_url) return false;
    if (isAdmin || isGroupAdmin) return true;
    return !!record.createdBy && record.createdBy.toLowerCase() === userEmail.toLowerCase();
  };

  const canUserUploadDoc = (record: Record) =>
    !isAdmin &&
    !isGroupAdmin &&
    !record.doc_url &&
    !!record.createdBy &&
    record.createdBy.toLowerCase() === userEmail.toLowerCase();

  const canAdminUploadDoc = (record: Record) =>
    (isAdmin || isGroupAdmin) && !record.doc_url;

  console.log('IncomeExpensesList - Total records:', records.length);
  console.log('IncomeExpensesList - Filtered records:', filteredRecords.length);
  console.log('IncomeExpensesList - All records with statuses:', records.map(r => ({
    id: r.id,
    status: r.status || 'NO STATUS',
    createdBy: r.createdBy,
    personName: r.personName,
    hasStatusField: 'status' in r
  })));

  const timeFilters = [
    'quarter_one',
    'quarter_two',
    'quarter_three',
    'quarter_four',
    'current_month',
    'current_fortnight',
    'this_year'
  ];

  const summaryCards = [
    {
      title: 'Income',
      value: summary?.total_actual_income || 0,
      icon: <AccountBalanceWallet />,
      color: 'success' as const,
      count: summary?.income_count || 0,
      trendDirection: 'up' as const
    },
    {
      title: 'Expenses',
      value: summary?.total_actual_expense || 0,
      icon: <Receipt />,
      color: 'error' as const,
      count: summary?.expense_count || 0,
      trendDirection: 'down' as const
    },
    {
      title: 'Group Capacity',
      value: summary?.group_capacity || 0,
      icon: <Groups />,
      color: 'info' as const,
      trendDirection: 'neutral' as const
    },
    {
      title: 'My Capacity',
      value: summary?.my_capacity || 0,
      icon: <Person />,
      color: 'secondary' as const,
      trendDirection: 'neutral' as const
    }
  ];

  const handleSnackbarClose = () => {
    setSnackbar((prev: typeof snackbar) => ({ ...prev, open: false }));
    dispatch(clearRecordsError());
  };

  // Delete confirmation dialog component
  const renderDeleteConfirmDialog = () => (
    <Dialog
      open={deleteConfirmOpen}
      onClose={handleCancelDelete}
      fullWidth
      maxWidth="xs"
      TransitionComponent={Transition}
    >
      <DialogTitle>
        <Typography variant="h6">Confirm Deletion</Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete the record for{' '}
          <strong>{recordToDelete?.personName}</strong> with amount{' '}
          <strong>${recordToDelete?.actAmt.toLocaleString()}</strong>? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelDelete} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmDelete}
          variant="contained"
          color="error"
          sx={{ textTransform: 'none' }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Show different states based on selection
  if (!selectedProjectId) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ p: isMobile ? 1 : 2, minHeight: '100vh' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            <Typography variant="h6" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
              Please select a project to view financial records.
            </Typography>
          </Box>
        </Box>
      </LocalizationProvider>
    );
  }

  if (!selectedServiceTitle) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ p: isMobile ? 1 : 2, minHeight: '100vh' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            <Typography variant="h6" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
              Please select a service to view financial records.
            </Typography>
          </Box>
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: isMobile ? 1 : 2, minHeight: '100vh' }}>
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
          {/* Header Section */}
          <Grid container spacing={1} sx={{ mb: 2 }} alignItems="center">
            {/* Title */}
            <Grid item xs={12} md={6}>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600}>
                Financial Overview
              </Typography>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<ImportExport />}
                  onClick={handleOpenImportModal}
                  size="small"
                >
                  Import
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenModal}
                  size="small"
                >
                  Add Record
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {/* Filter Section */}
          <Grid container spacing={1} sx={{ mb: 1 }} alignItems="center">
            {/* Year Picker */}
            <Grid item xs={12} sm={6} md={3} sx={{ mb: isMobile ? 1 : 0 }}>
              <FormControl fullWidth size="small">
                <DatePicker
                  views={['year']}
                  label="Year"
                  value={dayjs(current_year)}
                  onChange={(date) => {
                    if (date) {
                      dispatch(setCurrentYear(date.year().toString()));
                    }
                  }}
                  minDate={dayjs('1900')}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      variant: 'outlined',
                      fullWidth: true
                    }
                  }}
                />
              </FormControl>
            </Grid>

            {/* Time Period Select */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={time_filter}
                  label="Time Period"
                  onChange={(e: any) => {
                    const value = e.target.value as
                      | 'quarter_one'
                      | 'quarter_two'
                      | 'quarter_three'
                      | 'quarter_four'
                      | 'current_month'
                      | 'current_fortnight'
                      | 'this_year'
                      | null;
                    dispatch(setTimeFilter(value === 'this_year' ? null : value));
                  }}
                >
                  {timeFilters.map((filter) => (
                    <MenuItem key={filter} value={filter}>
                      {filter.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {summaryCards.map((card, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <SummaryCard
                  title={card.title}
                  value={card.value}
                  icon={<IconWrapper icon={card.icon} color={card.color} isMobile={isMobile} />}
                  color={card.color}
                  count={card.count}
                  trendDirection={card.trendDirection}
                />
              </Grid>
            ))}
          </Grid>

          {/* Filter Tabs */}
          <Paper
            sx={{
              mb: 2,
              borderRadius: 1,
              position: 'sticky',
              top: 60,
              zIndex: 10,
              backgroundColor: 'background.paper'
            }}
          >
            <Tabs
              value={filter}
              onChange={(e, newValue) => dispatch(setFilter(newValue))}
              variant={isMobile ? 'scrollable' : 'fullWidth'}
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.primary.main,
                  height: 2
                }
              }}
            >
              <Tab label="All" value="all" sx={{ textTransform: 'none', minWidth: 80 }} />
              <Tab
                label="Income"
                value="income"
                icon={<TrendingUp fontSize="small" />}
                iconPosition="start"
                sx={{ textTransform: 'none', minWidth: 80 }}
              />
              <Tab
                label="Expenses"
                value="expense"
                icon={<TrendingDown fontSize="small" />}
                iconPosition="start"
                sx={{ textTransform: 'none', minWidth: 80 }}
              />
            </Tabs>
          </Paper>

          {/* Data Display */}
          {loading && !records.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : records.length === 0 ? (
            <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
              No records found for the selected criteria.
            </Typography>
          ) : (
            <>
              {isMobile ? (
                <Stack spacing={1}>
                  {filteredRecords.map((record: Record, index: number) => (
                    <Card key={record.id || index} sx={{ p: 1.5 }}>
                      <Stack spacing={0.5}>
                        <Typography variant="subtitle2" noWrap>
                          {record.personName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {record.category}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={record.transactionType}
                            size="small"
                            color={record.transactionType === 'income' ? 'success' : 'error'}
                            icon={
                              record.transactionType === 'income' ? (
                                <TrendingUp fontSize="inherit" />
                              ) : (
                                <TrendingDown fontSize="inherit" />
                              )
                            }
                          />
                          <Typography variant="body2" sx={{ ml: 'auto' }}>
                            ${record.actAmt.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Variance:
                            <Typography
                              component="span"
                              variant="caption"
                              color={record.variance >= 0 ? 'success.main' : 'error.main'}
                              sx={{ ml: 0.5 }}
                            >
                              ${record.variance.toLocaleString()}
                            </Typography>
                          </Typography>
                          <Box>
                            {canViewDocument(record) && (
                              <Tooltip title="View Document">
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(record.doc_url as string, '_blank')}
                                  sx={{ p: 0.5, color: 'info.main' }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(record)}
                              sx={{ p: 0.5, color: 'primary.main' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(record)}
                              disabled={!record.id}
                              sx={{ p: 0.5 }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        {record.doc_url && (
                          <Chip
                            label="Document Uploaded"
                            color="success"
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                        {(canAdminUploadDoc(record) || canUserUploadDoc(record)) && (
                          <Box sx={{ mt: 0.5 }}>
                            <DocumentUpload
                              value={record.doc_url}
                              onChange={(file) => handleUploadDocument(record, file)}
                              label="Upload Document"
                              accept="image/*,application/pdf"
                              disabled={loading}
                            />
                          </Box>
                        )}
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    overflowX: 'auto',
                    '& td, & th': { whiteSpace: 'nowrap' }
                  }}
                >
                  <Table stickyHeader size={isTablet ? 'small' : 'medium'}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 150 }}>Person</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
                        <TableCell sx={{ minWidth: 100 }}>Type</TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>
                          Projected
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>
                          Actual
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>
                          Variance
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>Document</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRecords.map((record, index) => (
                        <TableRow key={record.id || index} hover>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {record.personName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {record.personEmail}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {record.category}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={record.transactionType}
                              size="small"
                              color={record.transactionType === 'income' ? 'success' : 'error'}
                              icon={
                                record.transactionType === 'income' ? (
                                  <TrendingUp fontSize="small" />
                                ) : (
                                  <TrendingDown fontSize="small" />
                                )
                              }
                            />
                          </TableCell>
                          <TableCell align="right">${record.projAmt.toLocaleString()}</TableCell>
                          <TableCell align="right">${record.actAmt.toLocaleString()}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 500,
                              color: record.variance >= 0 ? 'success.main' : 'error.main'
                            }}
                          >
                            ${record.variance.toLocaleString()}
                          </TableCell>
                          <TableCell align="center">
                            <Stack spacing={1} alignItems="center">
                              {canViewDocument(record) && (
                                <Tooltip title="View Document">
                                  <IconButton
                                    size="small"
                                    onClick={() => window.open(record.doc_url as string, '_blank')}
                                    sx={{ color: 'info.main' }}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {record.doc_url && <Chip label="Document Uploaded" color="success" size="small" />}
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            {(canAdminUploadDoc(record) || canUserUploadDoc(record)) && (
                              <Box sx={{ minWidth: 220 }}>
                                <DocumentUpload
                                  value={record.doc_url}
                                  onChange={(file) => handleUploadDocument(record, file)}
                                  label="Upload Document"
                                  accept="image/*,application/pdf"
                                  disabled={loading}
                                />
                              </Box>
                            )}
                            <IconButton
                              onClick={() => handleEdit(record)}
                              size="small"
                              sx={{ color: 'primary.main' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteClick(record)}
                              size="small"
                              disabled={!record.id}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {hasMoreRecords && nextToken && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <Button
                    variant="text"
                    onClick={handleLoadMore}
                    disabled={loading}
                    size="small"
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                  >
                    Load More
                  </Button>
                </Box>
              )}
            </>
          )}

          <IncomeExpensesForm open={openModal} onClose={handleCloseModal} editData={editData} />
          <IncomeExpensesImportModal open={openImportModal} onClose={handleCloseImportModal} />


          {/* Delete Confirmation Dialog */}
          {renderDeleteConfirmDialog()}

          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              severity={snackbar.severity}
              onClose={handleSnackbarClose}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default IncomeExpensesList;
